'use client'

import { urlForImage } from "@/sanity/lib/image"
import clsx from 'clsx'
import Autoplay from 'embla-carousel-autoplay'
import ClassNames from 'embla-carousel-class-names'
import useEmblaCarousel from 'embla-carousel-react'
import Fade from 'embla-carousel-fade'
import Image from "next/image"
import Link from 'next/link'
import {
	useEffect,
	useState
} from 'react'

interface TestimonialCarouselProps {
	testimonials: any
}

export default function TestimonialCarousel({testimonials}:TestimonialCarouselProps) {

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		containScroll: false,
		loop: true,
		duration: 35
	}, [Autoplay({delay: 4000}), ClassNames(), Fade()])
	const [selectedIndex, setSelectedIndex] = useState<number>(0)
	const [isAutoplaying, setIsAutoplaying] = useState<boolean>(true)

	useEffect(() => {
		if (!emblaApi) return

		const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap())
		emblaApi.on('select', onSelect)

		return () => {
			emblaApi.off('select', onSelect)
		}
	}, [emblaApi])

	useEffect(() => {
		if (!emblaApi) return

		const onPointerDown = () => {
			const isAutoplaying = emblaApi.plugins().autoplay.isPlaying()
			setIsAutoplaying(isAutoplaying)
		}
		emblaApi.on('pointerDown', onPointerDown)

		return () => {
			emblaApi.off('pointerDown', onPointerDown)
		}
	}, [emblaApi])

	const slideTo = (index) => {
		if (!emblaApi) return
		emblaApi?.scrollTo(index)
	}

	return (
		<div className={`content-block content-block--media-list-grid w-full px-site-margin my-[10rem]`}>
			<div className="bg-blackBg text-white rounded-weak overflow-clip">
				<div ref={emblaRef} className="embla relative w-full overflow-clip cursor-grab [&.is-dragging]:cursor-grabbing">
					<div className="embla__container w-full flex overflow-clip">
						{testimonials?.map((item, index) => (
							<div
								key={`testimonial-${index}`}
								className={`embla__slide flex-none w-full flex items-center justify-center py-36 md:py-48`}>
								<div className="absolute top-0 left-0 p-6 md:p-8 w-1/4 sm:site-grid-span-2 md:hidden">
									<Image
										className={`w-full h-auto`}
										src={
											!item?.image?.asset?.url?.includes('.svg')
												? urlForImage(item?.image).url()
												: item?.image?.asset?.url
										}
										width={
											!item?.image?.asset?.url?.includes('.svg') ? item?.image?.asset?.metadata?.dimensions?.width : 100
										}
										height={
											!item?.image?.asset?.url?.includes('.svg')
												? item?.image?.asset?.metadata?.dimensions?.height
												: 100
										}
										sizes="(min-width: 712px) 20vw, 30vw"
										alt={item?.image?.alt || ''}
										placeholder="blur"
										blurDataURL={item?.image?.asset?.metadata?.lqip}
									/>
								</div>

								<div className="w-full sm:site-grid-span-8 md:site-grid-span-6 px-site-margin sm:px-0">
									<div>
										<p
											className={`${item?.name ? 'style-body--lg md:style-body--lg relative blockquote' : 'style-h2'} text-balance"`}
											dangerouslySetInnerHTML={{ __html: item?.quote?.toString()?.replaceAll('\n', '<br />') }}
										/>
										{item?.name && (
											<p
												className={`style-h4 text-balance mt-6 relative pl-7 before:content-['—'] after:content-[close-quote] before:absolute before:left-0`}>
												{item?.name}
												{item?.position && (
													<>
														<br />
														{item?.position}
													</>
												)}
											</p>
										)}
									</div>

									{item?.link && (
										<div className="absolute bottom-0 left-0 md:left-auto md:right-0 p-6 md:p-8">
											<Link href={`/work/${item?.link?.slug?.current}`} className="style-h4 text-white">
												View case study
											</Link>
										</div>
									)}
								</div>
							</div>
						))}
					</div>

					<div
						className={`hidden md:flex absolute top-0 left-0 pt-6 md:pt-8 pl-6 md:pl-8 w-1/3 md:w-[10rem] h-auto flex-col gap-6`}>
						{testimonials.map((item, index) => (
							<button
								key={`logo-${index}`}
								onClick={() => slideTo(index)}
								className={`block mb-4 transition-all duration-150 ${index === selectedIndex ? 'opacity-100' : 'opacity-50 hover:opacity-90'}`}>
								<Image
									className={`w-full h-auto`}
									src={
										!item?.image?.asset?.url?.includes('.svg')
											? urlForImage(item?.image).url()
											: item?.image?.asset?.url
									}
									width={
										!item?.image?.asset?.url?.includes('.svg') ? item?.image?.asset?.metadata?.dimensions?.width : 100
									}
									height={
										!item?.image?.asset?.url?.includes('.svg') ? item?.image?.asset?.metadata?.dimensions?.height : 100
									}
									sizes="(min-width: 712px) 20vw, 30vw"
									alt={item?.image?.alt || ''}
									placeholder="blur"
									blurDataURL={item?.image?.asset?.metadata?.lqip}
								/>
							</button>
						))}
					</div>

					<div
						className={clsx(`dots absolute top-0 right-0 md:top-auto md:right-auto md:bottom-0 md:left-0 p-6 md:p-8`, {
							"autoplaying": isAutoplaying
						})}>
						<div className="flex gap-2">
							{testimonials.map((_, index) => (
								<button
									key={`dot-${index}`}
									onClick={() => slideTo(index)}
									className={`dot block h-[4px] bg-white/50 rounded-full overflow-clip transition-all duration-150 before:content-[''] before:block before:h-[4px] before:bg-white ${index === selectedIndex ? 'w-10 selected before:opacity-100' : 'w-5 before:opacity-0'}`}
								/>
							))}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
